import * as angular from 'angular';
import * as moment from 'moment';
import * as _ from 'lodash';

import { Helper } from '../../common/helpers/helper';
import { State, ReferenceState, ExtendedState } from '../../models';
import { IGroupService } from './';
import { ILoggerService, IDateRangeService } from '../general';
import { IwidgetIntervalHelperService } from '../helpers';
import { IRequestService } from '../../services';

export interface IReferenceStateService {
    getNewReferenceState(groupGuid: string, stateGuid: string): angular.IPromise<ExtendedState>;
    getReferenceStateAreaConfiguration(rs: any): any;
    updateOne(groupGuid: string, item: any): angular.IPromise<any>;
    updateReferenceState(guid: string, referenceState: ReferenceState): angular.IPromise<ReferenceState>;
    deleteReferenceState(groupGuid: string, referenceState: any): angular.IPromise<any>;
    buildConfiguration(referenceGuid: string, state: State, beginDateTime, endDateTime);
    checkOverlap(beginDate: moment.Moment, endDate: moment.Moment, guid: string,
        existing: any[], checkLeft: boolean, checkRight: boolean): [moment.Moment, moment.Moment];
}

export class ReferenceStateService implements IReferenceStateService {
    static serviceId = 'referenceStateService';

    static $inject = [
        '$q',
        'loggerService',
        'dateRangeService',
        'groupService',
        'widgetIntervalHelperService',
        'requestService'
    ];

    constructor(
        private _$q: angular.IQService,
        private _loggerService: ILoggerService,
        private _dateRangeService: IDateRangeService,
        private _groupService: IGroupService,
        private _widgetIntervalHelperService: IwidgetIntervalHelperService,
        private _requestService: IRequestService) {
    }

    updateOne(groupGuid: string, item: any): angular.IPromise<any> {
        const referenceState: any = {};
        referenceState.stateGuid = item.stateGuid;
        referenceState.GUID = item.guid;
        referenceState.beginDateTime = moment(item.beginDateTime).toISOString();
        referenceState.endDateTime = moment(item.endDateTime).toISOString();
        referenceState.olsonTimeZone = 'UTC';
        referenceState.longComment = item.comment;
        this.cleanRefStateCache(groupGuid);
        return this.updateReferenceState(groupGuid, referenceState);
    }

    updateReferenceState(guid: string, referenceState: any): angular.IPromise<any> {
        const url = this._requestService.getUrlV1() + 'group/addOrUpdateReferenceState/' + guid + '/';
        this.cleanRefStateCache(guid);
        return this._requestService.post(url, referenceState);
    }

    deleteReferenceState(groupGuid: string, referenceState: any): angular.IPromise<any> {
        const url = this._requestService.getUrlV1() + 'group/addOrUpdateReferenceState/' + groupGuid + '/';
        this.cleanRefStateCache(groupGuid);
        return this._requestService.delete(url, referenceState);
    }

    getReferenceStateAreaConfiguration(rs: any): any {
        return {
            name: rs.name,
            color: rs.color,
            beginDate: moment(rs.beginDateTime).toDate(),
            endDate: moment(rs.endDateTime).toDate(),
            stateGuid: rs.stateGuid,
            guid: rs.guid,
            comment: rs.comment
        };
    }

    getNewReferenceState(groupGuid: string, stateGuid: string): angular.IPromise<ExtendedState> {
        this._loggerService.log('Computing position of new reference state.', false);
        const defered = this._$q.defer<ExtendedState>();
        this._groupService
            .getReferenceStates(groupGuid)
            .then(referenceStates => {
                let beginDate = moment(this._dateRangeService.beginDate() as string);
                let endDate = moment(this._dateRangeService.endDate() as string);
                const guid = Helper.getGuid();
                let adjusted = [beginDate, endDate];
                if (referenceStates.length > 0) {
                    const mapped: any[] = referenceStates.map(d => {
                        return {
                            beginDateTime: moment(d.beginDateTime),
                            endDateTime: moment(d.endDateTime),
                            guid: d.guid
                        };
                    });
                    adjusted = this.checkOverlap(beginDate, endDate, guid, mapped, true, true);
                }
                const refStateRange = adjusted[1].diff(adjusted[0]);
                const totalRange = endDate.diff(beginDate);
                beginDate = adjusted[0];
                endDate = adjusted[1];
                if (refStateRange > 0.1 * totalRange) {
                    endDate = beginDate.clone().add(moment.duration(0.1 * totalRange));
                }
                const result: ExtendedState = {
                    beginDateTime: beginDate.toDate().toISOString(),
                    endDateTime: endDate.toDate().toISOString(),
                    guid: guid,
                    isNew: true,
                    state: undefined,
                    stateGuid: stateGuid
                };
                this._loggerService.log('Position of new reference state computed.', false);
                defered.resolve(result);
            })
            .catch((reason) => {
                this._loggerService.logError(reason);
                defered.reject(reason);
            });
        return defered.promise;
    }

    buildConfiguration(referenceGuid: string, state: State, beginDateTime, endDateTime) {
        return {
            name: state.name,
            stateGuid: state.guid,
            guid: referenceGuid,
            color: state.markerColorString,
            beginDate: moment(beginDateTime),
            endDate: moment(endDateTime)
        };
    }

    checkOverlap(beginDate: moment.Moment, endDate: moment.Moment, guid: string,
        existing: any[], checkLeft: boolean, checkRight: boolean): [moment.Moment, moment.Moment] {
        const overlap = _.sortBy(existing.filter(rs => rs.beginDateTime < endDate
            && rs.endDateTime > beginDate
            && rs.guid !== guid), rs => rs.beginDateTime);
        let newBeginDate = beginDate;
        let newEndDate = endDate;
        if (overlap && overlap.length > 0) {
            const nonAdjacent = overlap.filter(rs => _.every(overlap, o => o.beginDateTime.toISOString() !== rs.endDateTime.toISOString()));

            if (checkRight) {
                const right = nonAdjacent.filter(rs => rs.beginDateTime < endDate && rs.beginDateTime > beginDate);
                if (right.length > 0) {
                    const firstRight = right[0];
                    newEndDate = firstRight.beginDateTime;
                }
            }

            if (checkLeft) {
                const left = nonAdjacent.filter(rs => rs.endDateTime > beginDate && rs.endDateTime < newEndDate);
                if (left.length > 0) {
                    const lastLeft = [...left].reverse()[0];
                    newBeginDate = lastLeft.endDateTime;
                }
            }
        }
        const result: [moment.Moment, moment.Moment] = [newBeginDate, newEndDate];
        return result;
    }


    private cleanRefStateCache(guid: string) {
        if (this._dateRangeService.initialized()) {
            const defaultInterval = this._widgetIntervalHelperService.getCurrentInclusiveInterval();
            const url = this._requestService.getUrlV1() + 'group/referenceStates/?groupGuid=' + guid + '&beginDate=' + defaultInterval.beginDate + '&endDate=' + defaultInterval.endDate;
            this._requestService.cleanCache(url);
        }
    }

}
